import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ClinicCategory {
  id: string;
  name: string;
  created_at: string;
}

export function useClinicCategories() {
  const { profile } = useAuth();

  const query = useQuery({
    queryKey: ['clinic-categories', profile?.clinic_id],
    queryFn: async () => {
      if (!profile?.clinic_id) return [];
      const { data, error } = await supabase
        .from('clinic_categories')
        .select('id, name, created_at')
        .eq('clinic_id', profile.clinic_id)
        .order('name');
      if (error) throw error;
      return (data ?? []) as ClinicCategory[];
    },
    enabled: !!profile?.clinic_id,
  });

  return query;
}

export function useAddCategory() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      if (!profile?.clinic_id) throw new Error('No clinic');
      const { error } = await supabase
        .from('clinic_categories')
        .insert({ clinic_id: profile.clinic_id, name: name.trim() });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-categories', profile?.clinic_id] });
    },
  });
}

export function useDeleteCategory() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryId: string) => {
      const { error } = await supabase
        .from('clinic_categories')
        .delete()
        .eq('id', categoryId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinic-categories', profile?.clinic_id] });
    },
  });
}

export async function getCategoryPolicyCount(categoryName: string, clinicId: string): Promise<number> {
  const { count, error } = await supabase
    .from('policies')
    .select('id', { count: 'exact', head: true })
    .eq('clinic_id', clinicId)
    .eq('category', categoryName);
  if (error) return 0;
  return count ?? 0;
}
